import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import { PartialDeep } from "type-fest";
import { IdName } from "types/common-types";
import { AccommodationMaintenanceTypeDatalistQuery, AccommodationMaintenanceTypeDatalistQueryVariables, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput } from "types/graphql";
import * as lodash from "lodash";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import { graph } from "config/axios-context";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";
import { Roles } from "utilities/app-utilities";
import ModalPortal from "components/modal/ModalPortal";
import { useSearchParams } from "react-router-dom";
import AccommodationMaintenanceTypeFrom from "./AccommodationMaintenanceTypeFrom";
import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from "react";
import { deleteMutation } from "utilities/entities/common/delete-mutation";

const FORM_MODAL_ID = "accommodation-maintenance-type-datalist-props"

const query = gql`
query AccommodationMaintenanceTypeDatalist($where: AccommodationMaintenanceTypeFilterInput, $order: [AccommodationMaintenanceTypeSortInput!], $skip: Int, $take:Int) {
    accommodationMaintenanceTypes {
      items(where: $where, order: $order, skip: $skip, take: $take) {
        name
        id,
        logs {
          any
        }
      }
      count(where: $where)
    }
  }
`;

type DataItem = IdName & {
    logs: {
        any: boolean
    }
}

type AccommodationMaintenanceTypeDatalistProps = {
    config?: PartialDeep<DatalistPropsType<DataItem, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput>>,
    name: string,
    fixedFilters?: AccommodationMaintenanceTypeFilterInput;
}

const AccommodationMaintenanceTypeDatalist = forwardRef((props: AccommodationMaintenanceTypeDatalistProps, ref: ForwardedRef<{ openForm: () => void }>) => {
    const context = useAppContext();
    const [searchParams] = useSearchParams();
    const formModalId = FORM_MODAL_ID + '-' + props.name;
    const datalistRef = useRef<Datalist<DataItem, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput>>(null);
    const datalistDefaultConfig: DatalistPropsType<DataItem, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput> = {
        name: props.name + "-AccommodationMaintenanceTypeDatalist",
        itemId(i) {
            return i.id;
        },
        dataSource: {
            request: async (params) => {
                let variables: AccommodationMaintenanceTypeDatalistQueryVariables = {
                    where: params.filters,
                    order: params.sort,
                    skip: params.skip,
                    take: params.take
                }

                if (params.searchText) {
                    const search = {
                        name: {
                            contains: params.searchText
                        }
                    };

                    variables.where = appendFilters(variables.where, search);
                }

                if (props.fixedFilters) {
                    variables.where = appendFilters(variables.where, props.fixedFilters);
                }

                try {
                    const response = await graph<AccommodationMaintenanceTypeDatalistQuery, AccommodationMaintenanceTypeDatalistQueryVariables>({
                        query,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    return response.data?.accommodationMaintenanceTypes!;
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
        },
        table: {
            columns: [
                {
                    name: 'id',
                    card: {
                        group: 'main',
                        icon: faHashtag
                    }
                },
                {
                    name: 'name',
                    sort: true,
                    card: {
                        group: 'main'
                    }
                }
            ],
            render: {
                actions: i => ({
                    mutationPermissionRole: Roles.AGRICULTURE_WORK_WRITE,
                    delete: !i.logs.any ? undefined : async (item: DataItem, e: React.MouseEvent) => {
                        await deleteMutation('accommodationMaintenanceTypeDelete', item.id);
                    },
                    editUrl: {
                        type: 'modal',
                        url: formModalId,
                        params: i => i,
                    }
                })
            }
        }
    }

    useImperativeHandle(ref, () => ({
        openForm: () => context.modal.open(formModalId),
    }));

    const config = lodash.merge(datalistDefaultConfig, props.config);
    return (
        <>
            <Datalist {...config} ref={datalistRef} />
            <ModalPortal name={formModalId} title={'pages.accommodation.work.maintenance-type.form'}>
                {searchParams.has(formModalId) ? (
                    <AccommodationMaintenanceTypeFrom
                        model={context.modal.getData(formModalId)}
                        name={props.name + formModalId + "-form"}
                        onSubmit={() => {
                            context.navigate(-1);
                            datalistRef.current?.reload()
                        }}
                    />
                ) : <></>}
            </ModalPortal>
        </>
    );
});

export default AccommodationMaintenanceTypeDatalist;
